import { Capacitor, registerPlugin } from '@capacitor/core';
import { isDesktopBrowser } from '../../utils/isDesktopBrowser';

// 2026-05-30 — Plugin nativo Volume (registrado em MainActivity.java → VolumePlugin.java)
// Substitui `capacitor-volume-control@0.0.1` que era peerDeps `^6.0.0` e quebrou
// silenciosamente em Capacitor 7. Vide .claude/rules/plataformas-suportadas.md.
//
// API:
//   getVolume()                        → { volume: 0-100, raw, max }
//   setVolume({ volume: 0-100 })       → { volume, applied_raw, max }
//   adjustVolume({ direction: up/down })
interface VolumePluginAPI {
  getVolume(): Promise<{ volume: number; raw: number; max: number }>;
  setVolume(options: {
    volume: number;
  }): Promise<{ volume: number; applied_raw: number; max: number }>;
  adjustVolume(options: { direction: 'up' | 'down' }): Promise<{
    volume: number;
    raw: number;
    max: number;
    direction: string;
  }>;
}

const VolumeNative = registerPlugin<VolumePluginAPI>('Volume');

// window.tizen e window.webOS já são declarados via `declare global` em
// TizenPlatformService.ts e WebOSPlatformService.ts. Aqui usamos
// window.tizen / window.webOS direto — sem `declare const tizen: any` que
// criava um any global escondido.

export class VolumeService {
  private static instance: VolumeService;

  // Último valor de mute EFETIVAMENTE aplicado no sistema (null = nunca aplicado).
  // No Tizen, TODA chamada de `tvaudiocontrol.setMute()` mostra o OSD nativo de mudo —
  // mesmo com o valor IGUAL. Um efeito no Stream re-espelha `isMuted` a cada troca de
  // slot (~4s) → o OSD piscava sem parar. Guardar o último valor e só chamar a API quando
  // MUDAR mata o flash (dedup). Vide setMute().
  private lastAppliedMute: boolean | null = null;

  private constructor() {}

  public static getInstance(): VolumeService {
    if (!VolumeService.instance) {
      VolumeService.instance = new VolumeService();
    }
    return VolumeService.instance;
  }

  /**
   * Set volume level (0-100)
   */
  public async setVolume(level: number): Promise<void> {
    const safeLevel = Math.max(0, Math.min(100, Math.round(level)));
    console.log(`[VolumeService] Setting volume to ${safeLevel}%`);

    try {
      if (this.isDesktopBrowser()) {
        console.warn(
          '[VolumeService] Volume control disabled on desktop browsers',
        );
        return;
      }
      if (this.isTizen()) {
        this.setTizenVolume(safeLevel);
      } else if (this.isWebOS()) {
        await this.setWebOSVolume(safeLevel);
      } else if (this.isAndroid()) {
        await this.setAndroidVolume(safeLevel);
      } else {
        console.warn(
          '[VolumeService] Platform not supported for volume control',
        );
      }
    } catch (error) {
      console.error('[VolumeService] Failed to set volume:', error);
    }
  }

  /**
   * Último volume JÁ LOGADO, por plataforma. `getVolume()` é chamado em POLLING
   * (o monitor de auto-reset do ConfigManager), então logar a cada chamada
   * inundava o {@link LogRingBuffer} — 500 entradas, todas de volume, e o
   * `logs:tail` remoto ficava inútil pra diagnosticar qualquer outra coisa
   * (medido em campo no RD-Raia, 2026-09-19: o buffer inteiro era
   * `[VolumeService] Android volume: 0` repetido).
   *
   * ⚠️ Rebaixar pra `console.debug` NÃO resolveria: o ring buffer captura
   * `debug` também. O log precisa simplesmente não acontecer quando nada mudou.
   */
  private lastLoggedVolume: number | null = null;

  /** Loga só na TRANSIÇÃO de valor — é isso que interessa no troubleshooting. */
  private logIfChanged(platform: string, volume: number): number {
    if (volume !== this.lastLoggedVolume) {
      console.log(
        `[VolumeService] ${platform} volume: ${this.lastLoggedVolume ?? '—'} → ${volume}`,
      );
      this.lastLoggedVolume = volume;
    }
    return volume;
  }

  /**
   * Get current volume level (0-100)
   */
  public async getVolume(): Promise<number> {
    try {
      if (this.isDesktopBrowser()) {
        console.warn(
          '[VolumeService] Volume control disabled on desktop browsers',
        );
        return -1;
      }
      if (this.isTizen()) {
        return this.logIfChanged('Tizen', this.getTizenVolume());
      } else if (this.isWebOS()) {
        return this.logIfChanged('WebOS', await this.getWebOSVolume());
      } else if (this.isAndroid()) {
        return this.logIfChanged('Android', await this.getAndroidVolume());
      }
    } catch (error) {
      console.error('[VolumeService] Failed to get volume:', error);
    }
    return -1;
  }

  /**
   * Set the SYSTEM mute state (0-level agnostic on/off).
   *
   * <p>Needed by the AVPlay backend (Tizen): the AVPlay video plays on a native
   * HW plane that has NO per-instance mute/volume API — so the app's mute intent
   * ({@code streamStore.isMuted}) must be mirrored onto the system audio, else
   * AVPlay would play sound even when the `<video>` path would be silent.</p>
   *
   * <p>The `<video>` element path (Android/browser + Tizen images/held-video)
   * mutes via the DOM `muted` attribute and does NOT rely on this. On those
   * platforms/paths this is a no-op. Only Tizen (and webOS for symmetry) touch
   * system mute — and only when the AVPlay backend flag is on (caller-gated).</p>
   */
  public async setMute(muted: boolean): Promise<void> {
    // DEDUP (anti-OSD): no Tizen, cada `tvaudiocontrol.setMute()` pisca o ícone de mudo
    // nativo — mesmo re-aplicando o MESMO valor. Como o Stream re-espelha `isMuted` a cada
    // troca de slot (~4s), sem isto o OSD piscava direto. Só aplica quando o valor MUDA.
    // A 1ª chamada sempre passa (lastAppliedMute começa null). Vale p/ Tizen+webOS;
    // Android/browser já é no-op aqui (comportamento externo idêntico).
    if (muted === this.lastAppliedMute) return;
    try {
      if (this.isTizen()) {
        window.tizen?.tvaudiocontrol?.setMute?.(muted);
        console.log(`[VolumeService] Tizen system mute → ${muted}`);
      } else if (this.isWebOS()) {
        await this.setWebOSMute(muted);
      }
      // Android/desktop browser: DOM `<video muted>` handles it — no system mute.
      // Só marca DEPOIS de aplicar com sucesso — se a API nativa lançar, re-tenta na próxima.
      this.lastAppliedMute = muted;
    } catch (error) {
      console.error('[VolumeService] Failed to set mute:', error);
    }
  }

  private setWebOSMute(muted: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url = 'luna://com.webos.service.audio/setMuted';
      window.webOS!.service!.request(url, {
        method: 'setMuted',
        parameters: { muted },
        onSuccess: () => resolve(),
        onFailure: (err) =>
          reject(
            new Error((err.errorText as string) || 'webOS setMuted failed'),
          ),
      });
    });
  }

  private isTizen(): boolean {
    return typeof window !== 'undefined' && !!window.tizen?.tvaudiocontrol;
  }

  private isWebOS(): boolean {
    return typeof window !== 'undefined' && !!window.webOS?.service;
  }

  private isAndroid(): boolean {
    return Capacitor.getPlatform() === 'android';
  }

  // Delega pro util compartilhado (utils/isDesktopBrowser) — FONTE ÚNICA.
  // A lógica de exclusão de Smart TV (fix 2026-06-06) vive lá agora; antes o
  // ConfigManager tinha uma cópia SEM esse fix e desabilitava o volume na Tizen.
  private isDesktopBrowser(): boolean {
    return isDesktopBrowser();
  }

  // --- Platform Specific Implementations ---

  private setTizenVolume(level: number) {
    try {
      window.tizen?.tvaudiocontrol?.setVolume?.(level);
    } catch (e) {
      console.error('[VolumeService] Tizen error:', e);
      throw e; // Propagate error
    }
  }

  private getTizenVolume(): number {
    try {
      return window.tizen?.tvaudiocontrol?.getVolume?.() ?? -1;
    } catch (e) {
      console.error('[VolumeService] Tizen get error:', e);
      return -1;
    }
  }

  private setWebOSVolume(level: number) {
    return new Promise<void>((resolve, reject) => {
      const url = 'luna://com.webos.service.audio/setVolume';
      const params = { volume: level };

      window.webOS!.service!.request(url, {
        method: 'setVolume',
        parameters: params,
        onSuccess: () => resolve(),
        onFailure: (err) =>
          reject(
            new Error((err.errorText as string) || 'webOS setVolume failed'),
          ),
      });
    });
  }

  private getWebOSVolume(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const url = 'luna://com.webos.service.audio/getVolume';
      window.webOS!.service!.request(url, {
        method: 'getVolume',
        parameters: {},
        onSuccess: (res) => resolve(res.volume as number),
        onFailure: (err) =>
          reject(
            new Error((err.errorText as string) || 'webOS getVolume failed'),
          ),
      });
    });
  }

  /**
   * Android nativo via VolumePlugin.java (registrado em MainActivity).
   *
   * <p>O plugin usa {@code AudioManager.STREAM_MUSIC} — mesmo stream
   * configurado em {@code setVolumeControlStream(STREAM_MUSIC)} do
   * MainActivity, garantindo que JS e teclas físicas mexem no mesmo controle.</p>
   *
   * <p>Range: sempre 0-100, normalizado no lado nativo a partir do
   * {@code streamMaxVolume} (geralmente 15 em Android stock).</p>
   */
  private async setAndroidVolume(level: number) {
    try {
      await VolumeNative.setVolume({ volume: level });
    } catch (e) {
      console.warn('[VolumeService] Android native setVolume failed:', e);
    }
  }

  private async getAndroidVolume(): Promise<number> {
    try {
      // Sem log aqui: este método roda em polling e o chamador já loga na
      // transição (logIfChanged). O `raw` só interessa quando o valor muda,
      // e aí o log do chamador basta.
      const result = await VolumeNative.getVolume();
      return typeof result.volume === 'number' ? result.volume : -1;
    } catch (e) {
      console.error(
        '[VolumeService] Android native getVolume failed - returning default 50:',
        e,
      );
      return 50; // Fallback to 50%
    }
  }
}
